import React, { useEffect, useRef } from "react";
import * as THREE from "three";

// Golden particle cube: particles on the 6 cube faces, spring physics ripple on
// click/touch, continuous rotation and subtle mouse parallax.
const ParticleCubeHero = ({ className = "" }) => {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    const GRID = isMobile ? 16 : 22;   // particles per face edge (fewer on mobile)
    const HALF = 1.25;              // cube half-size
    const perFace = GRID * GRID;
    const N = perFace * 6;

    const base = new Float32Array(N * 3);
    const cur = new Float32Array(N * 3);
    const vel = new Float32Array(N * 3);

    // distribute on faces
    let idx = 0;
    const faces = [
      (u, v) => [u, v, HALF], (u, v) => [u, v, -HALF],
      (u, v) => [HALF, u, v], (u, v) => [-HALF, u, v],
      (u, v) => [u, HALF, v], (u, v) => [u, -HALF, v],
    ];
    for (const f of faces) {
      for (let i = 0; i < GRID; i++) {
        for (let j = 0; j < GRID; j++) {
          const u = (i / (GRID - 1) - 0.5) * 2 * HALF;
          const v = (j / (GRID - 1) - 0.5) * 2 * HALF;
          const [x, y, z] = f(u, v);
          const jx = (Math.random() - 0.5) * 0.02;
          const jy = (Math.random() - 0.5) * 0.02;
          const jz = (Math.random() - 0.5) * 0.02;
          base[idx * 3] = cur[idx * 3] = x + jx;
          base[idx * 3 + 1] = cur[idx * 3 + 1] = y + jy;
          base[idx * 3 + 2] = cur[idx * 3 + 2] = z + jz;
          idx++;
        }
      }
    }

    const width = mount.clientWidth;
    const height = mount.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(52, width / height, 0.1, 100);
    camera.position.set(0, 0, isMobile ? 7.6 : 4.6);
    const CAM_Z = isMobile ? 7.6 : 4.6;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobile ? 1.5 : 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);
    renderer.domElement.style.touchAction = "manipulation";

    // soft circular glow sprite
    const tc = document.createElement("canvas");
    tc.width = tc.height = 64;
    const ctx = tc.getContext("2d");
    const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    g.addColorStop(0, "rgba(255,244,214,1)");
    g.addColorStop(0.35, "rgba(210,190,130,0.85)");
    g.addColorStop(1, "rgba(178,164,117,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 64, 64);
    const sprite = new THREE.CanvasTexture(tc);

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(cur, 3));
    const material = new THREE.PointsMaterial({
      size: 0.05, map: sprite, transparent: true, depthWrite: false,
      blending: THREE.AdditiveBlending, color: new THREE.Color("#C7B78F"), sizeAttenuation: true,
    });
    const points = new THREE.Points(geometry, material);

    const group = new THREE.Group();
    group.add(points);
    // invisible box for accurate ripple raycasting
    const hitBox = new THREE.Mesh(
      new THREE.BoxGeometry(HALF * 2, HALF * 2, HALF * 2),
      new THREE.MeshBasicMaterial({ visible: false })
    );
    group.add(hitBox);
    group.rotation.set(-0.35, 0.6, 0);
    scene.add(group);

    // physics params
    const K = 7.0, DAMP = 2.3;
    const R_SPEED = 3.1, R_WIDTH = 0.5, R_AMP = 30, R_DECAY = 1.7, R_MAXAGE = 3.0;
    const ripples = [];

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    const targetRot = { x: 0, y: 0 };
    let mouseX = 0, mouseY = 0;

    const addRipple = (clientX, clientY) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointer, camera);
      const hit = raycaster.intersectObject(hitBox, false)[0];
      let origin;
      if (hit) {
        origin = group.worldToLocal(hit.point.clone());
      } else {
        origin = new THREE.Vector3(0, 0, 0);
      }
      ripples.push({ ox: origin.x, oy: origin.y, oz: origin.z, t0: performance.now() / 1000 });
      if (ripples.length > 6) ripples.shift();
    };

    const onPointerDown = (e) => {
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      addRipple(cx, cy);
    };
    const onPointerMove = (e) => {
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      mouseX = (cx / window.innerWidth) * 2 - 1;
      mouseY = (cy / window.innerHeight) * 2 - 1;
    };
    renderer.domElement.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);

    // initial welcome ripple
    const kickoff = setTimeout(() => ripples.push({ ox: 0, oy: 0, oz: 0, t0: performance.now() / 1000 }), 700);

    let raf;
    let last = performance.now();
    const posAttr = geometry.getAttribute("position");

    const tick = () => {
      const now = performance.now();
      let dt = (now - last) / 1000;
      last = now;
      if (dt > 0.033) dt = 0.033;
      const tsec = now / 1000;

      // clean old ripples
      for (let r = ripples.length - 1; r >= 0; r--) {
        if (tsec - ripples[r].t0 > R_MAXAGE) ripples.splice(r, 1);
      }

      for (let i = 0; i < N; i++) {
        const p = i * 3;
        let ax = -K * (cur[p] - base[p]) - DAMP * vel[p];
        let ay = -K * (cur[p + 1] - base[p + 1]) - DAMP * vel[p + 1];
        let az = -K * (cur[p + 2] - base[p + 2]) - DAMP * vel[p + 2];

        for (let r = 0; r < ripples.length; r++) {
          const rp = ripples[r];
          const age = tsec - rp.t0;
          const front = age * R_SPEED;
          const dx = base[p] - rp.ox, dy = base[p + 1] - rp.oy, dz = base[p + 2] - rp.oz;
          const d = Math.sqrt(dx * dx + dy * dy + dz * dz) + 1e-4;
          const gv = Math.exp(-((d - front) / R_WIDTH) * ((d - front) / R_WIDTH));
          if (gv > 0.002) {
            const amp = R_AMP * Math.exp(-age * R_DECAY) * gv;
            ax += (dx / d) * amp;
            ay += (dy / d) * amp;
            az += (dz / d) * amp;
          }
        }

        vel[p] += ax * dt; vel[p + 1] += ay * dt; vel[p + 2] += az * dt;
        cur[p] += vel[p] * dt; cur[p + 1] += vel[p + 1] * dt; cur[p + 2] += vel[p + 2] * dt;
      }
      posAttr.needsUpdate = true;

      // rotation + parallax
      group.rotation.y += 0.0026;
      targetRot.x += ((-mouseY * 0.28) - targetRot.x) * 0.05;
      targetRot.y += ((mouseX * 0.32) - targetRot.y) * 0.05;
      group.rotation.x = -0.35 + targetRot.x;
      camera.position.x += (mouseX * 0.4 - camera.position.x) * 0.04;
      camera.position.y += (-mouseY * 0.3 - camera.position.y) * 0.04;
      camera.position.z += (CAM_Z - camera.position.z) * 0.04;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const onResize = () => {
      const w = mount.clientWidth, h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    const ro = new ResizeObserver(onResize);
    ro.observe(mount);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(kickoff);
      ro.disconnect();
      renderer.domElement.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      geometry.dispose();
      material.dispose();
      sprite.dispose();
      hitBox.geometry.dispose();
      hitBox.material.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} className={className} data-testid="particle-cube" aria-hidden="true" />;
};

export default ParticleCubeHero;
